import { GraphReport } from '@dashhudson/dashing-ui/graph-report';
import { SingleMetricReport } from '@dashhudson/dashing-ui/single-metric-report';
import { TableMetricReport } from '@dashhudson/dashing-ui/table-metric-report';
import cloneDeep from 'lodash/cloneDeep';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import isObject from 'lodash/isObject';
import omit from 'lodash/omit';
import startCase from 'lodash/startCase';
import toNumber from 'lodash/toNumber';
import { defineAsyncComponent } from 'vue';

import { formatTypeMap } from '@/app/dashboards/composables/useMetricFormattingSetup';
import { DATA_TYPE_TIME_UNITS, TOTAL_METRIC_GROUPS } from '@/app/dashboards/constants';
import {
  AGGREGATION_OPTIONS,
  ALL_CONTENT,
  CREATOR_STATUS,
  CREATORS,
  DATE_PUBLISHED_SORT_ORDER_OPTIONS,
  DISPLAY_STRATEGY,
  METRIC_PREFIX,
  METRIC_TYPES,
  SORT_ORDER_OPTIONS,
  UGC,
} from '@/app/dashboards/reportConstants';
import { createTSICustomDateRange } from '@/app/dashboards/utils/date.utils';
import { V1LayoutAdapter } from '@/app/dashboards/utils/layout/V1LayoutAdapter';
import { getMediaTypeMetricParentName } from '@/app/dashboards/utils/media-types.utils';
import {
  CONTENT_REPORT_TYPES,
  GRAPH_REPORTS,
  isMultiMediaMetricReport,
  LINE_CHART_REPORTS,
  LINE_GRAPH_REPORT_TYPES,
  METRIC_TYPE_REPORT_KEYS,
  PRODUCT_CATEGORY_TO_REPORT_CATEGORY_MAP,
  REPORT_CATEGORY_OPTIONS,
  REPORTS,
  STACKED_BAR_CHART_REPORTS,
  TOTAL_REPORT_TYPES,
} from '@/app/dashboards/utils/reports.enum';
import {
  formatCompetitorTagsByBrand,
  getCompetitorSourceAccountIDsFromTags,
} from '@/app/dashboards/utils/tagging.utils';
import { DURATION_OPTIONS } from '@/components/dashingUI/constants/goal-input-constants';
import {
  AGGREGATE_REPORT_TYPES,
  BASE_BREAKDOWN_OPTIONS,
  COMMUNITY_STACKED_BAR_BREAKDOWN_OPTIONS,
  GRAPH_DISPLAY_OPTIONS_FOR_MENU,
  LISTENING_STACKED_BAR_BREAKDOWN_OPTIONS,
  METRIC_DISPLAY_OPTIONS,
  REPORT_CATEGORY_TO_REPORT_TYPE,
  TSI_STACKED_BAR_BREAKDOWN_OPTIONS,
} from '@/models/dashboards/aggregate-type.enum';
import { BENCHMARK_TYPES } from '@/models/dashboards/benchmark-type.enum';
import {
  ALL_CHANNELS_KEY,
  CHANNELS,
  getChannelText,
  getTsiEnabledChannels,
} from '@/models/dashboards/channels.enum';
import { DATA_TYPES, TOTAL_TSI_METRIC } from '@/models/dashboards/metrics.constants';
import { formatIntegerWithRules } from '@/utils/formatters';

const VIRTUAL_SCROLLER_HEIGHT = '30rem';
const VIRTUAL_SCROLLER_BUFFER = 10;
const VIRTUAL_SCROLLER_ITEM_SIZE = 32;

const FILTERS_VIRTUAL_SCROLLER_HEIGHT = '20rem';
const FILTERS_VIRTUAL_SCROLLER_LIMIT = 50;

const ContentReportPanel = defineAsyncComponent(
  () => import('@/app/dashboards/components/Report/Panels/ContentReportPanel.vue'),
);
const HeaderReportPanel = defineAsyncComponent(
  () => import('@/app/dashboards/components/HeaderReportPanel.vue'),
);
const NoteReportPanel = defineAsyncComponent(
  () => import('@/app/dashboards/components/Report/Panels/NoteReportPanel.vue'),
);
const VisionAiReportPanelPlaceHolder = defineAsyncComponent(
  () => import('@/app/dashboards/components/Report/Panels/VisionAiReportPanelPlaceHolder.vue'),
);

export function buildFilterOverrideParams({
  contentTagIds,
  contentTagMatchType,
  messageTagIds,
  messageTagMatchType,
  brandIds,
  brandTagIds,
  competitorSourceAccountIds,
  competitorTagIds,
  campaignIds,
  boardIds,
} = {}) {
  const params = {};

  const overrideContentTags = contentTagIds && contentTagIds.length > 0;
  const overrideContentTagMatchType = contentTagMatchType && overrideContentTags;
  const overrideMessageTags = messageTagIds && messageTagIds.length > 0;
  const overrideMessageTagMatchType = messageTagMatchType && overrideMessageTags;
  const overrideBrandIds = brandIds && brandIds.length > 0;
  const overrideBrandTagIds = brandTagIds && brandTagIds.length > 0;
  const overrideCompetitorIds = competitorSourceAccountIds && competitorSourceAccountIds.length > 0;
  const overrideCompetitorTagIds = competitorTagIds && competitorTagIds.length > 0;
  const overrideCampaignIds = campaignIds && campaignIds.length > 0;
  const overrideBoardIds = boardIds && boardIds.length > 0;

  if (overrideContentTags) params.override_content_tag_ids = contentTagIds.join(',');
  if (overrideContentTagMatchType) params.override_content_tag_match_type = contentTagMatchType;
  if (overrideMessageTags) params.override_message_tag_ids = messageTagIds.join(',');
  if (overrideMessageTagMatchType) params.override_message_tag_match_type = messageTagMatchType;
  if (overrideBrandIds) params.override_brand_ids = brandIds.join(',');
  if (overrideBrandTagIds) params.override_brand_tag_ids = brandTagIds.join(',');
  if (overrideCompetitorIds)
    params.override_competitor_source_account_ids = competitorSourceAccountIds.join(',');
  if (overrideCompetitorTagIds) params.override_competitor_tag_ids = competitorTagIds.join(',');
  if (overrideCampaignIds) params.override_campaign_ids = campaignIds.join(',');
  if (overrideBoardIds) params.override_board_ids = boardIds.join(',');

  return params;
}

export function getReportRequestParams(
  reportDateRangeInUserTimezone,
  contextDateRangeInUserTimezone,
  graphScale,
  contentTagIds,
  contentTagMatchType,
  messageTagIds,
  messageTagMatchType,
  brandIds,
  brandTagIds,
  competitorSourceAccountIds,
  competitorTagIds,
  campaignIds,
  boardIds,
) {
  return {
    include_data: true,
    start_date: reportDateRangeInUserTimezone?.[0],
    end_date: reportDateRangeInUserTimezone?.[1],
    context_start_date: contextDateRangeInUserTimezone?.[0],
    context_end_date: contextDateRangeInUserTimezone?.[1],
    time_scale: graphScale,
    ...buildFilterOverrideParams({
      contentTagIds,
      contentTagMatchType,
      messageTagIds,
      messageTagMatchType,
      brandIds,
      brandTagIds,
      competitorSourceAccountIds,
      competitorTagIds,
      campaignIds,
      boardIds,
    }),
  };
}

export function getBrandIdToReportAdCampaign(reportAdCampaigns) {
  return reportAdCampaigns.reduce((mapping, brandReport) => {
    mapping[brandReport.id] = brandReport;
    return mapping;
  }, {});
}

export function getFormattedMetrics(metricsObject, reportMetric, channel) {
  const channelKey = channel ?? ALL_CHANNELS_KEY;
  const metricValue =
    metricsObject?.[reportMetric]?.[channelKey] ?? metricsObject?.[reportMetric] ?? {};

  const formattedMetrics =
    Object.keys(metricsObject ?? {}).map((metric) => {
      const metricValues = metricsObject?.[metric]?.[channel] || metricsObject?.[metric] || {};
      return { metric, ...metricValues };
    }) || [];

  return {
    formattedMetrics,
    metric: metricValue,
  };
}

export function parseQueryToArray(query) {
  return query?.split(',').map((q) => q.trim());
}

export function formatReportVisualizationOptions(reportVisualizationOptions) {
  return reportVisualizationOptions.map((option) => omit(option, 'text', 'icon'));
}

export function cleanNestedMetaHelper(root) {
  if (!root) return null;

  Object.entries(root).forEach(([key, value]) => {
    if (isArray(value)) {
      const cleanedArray = value
        .map((item) => (isObject(item) ? cleanNestedMetaHelper(item) : item))
        .filter((item) => {
          if (isObject(item)) return Object.keys(item).length > 0;
          return !isNil(item) && item !== '';
        });

      if (cleanedArray.length === 0) {
        delete root[key];
      } else {
        root[key] = cleanedArray;
      }

      return;
    }

    if (isObject(value)) {
      const result = cleanNestedMetaHelper(value);
      if (!result || Object.keys(result).length === 0) delete root[key];
      return;
    }

    if (isNil(value) || value === '') delete root[key];
  });

  return Object.keys(root).length === 0 ? null : root;
}

export function cleanReportFilters(cleanedMeta) {
  if (cleanedMeta?.listening) {
    const cleaned = cleanNestedMetaHelper(cleanedMeta.listening);
    if (!cleaned) {
      delete cleanedMeta.listening;
    }
  }

  if (cleanedMeta?.community) {
    const cleaned = cleanNestedMetaHelper(cleanedMeta.community);
    if (!cleaned) {
      delete cleanedMeta.community;
    }
  }
}
/**
 * @param {object} params
 * @param {{ value?: string, isAveraged?: boolean }[]} [params.selectedMetrics]
 * @param {string[]} [params.channels]
 * @param {string} [params.metricReportTypeKey]
 * @param {{
 *   getTargetMetricDetails: Function,
 *   getTargetMetricDetailsMultiChannel: Function,
 * }} params.metricsStore
 */
export function getSelectedMetricsGoalState({
  selectedMetrics = [],
  channels = [],
  metricReportTypeKey,
  metricsStore,
}) {
  const isContentReport = metricReportTypeKey === METRIC_TYPE_REPORT_KEYS.CONTENT_REPORT;
  const isMultiChannel = channels.length > 1;

  const details = selectedMetrics.map((metric) => {
    const metricKey =
      metric?.isAveraged && metric?.value?.startsWith(METRIC_PREFIX.AVERAGE)
        ? metric.value.slice(METRIC_PREFIX.AVERAGE.length)
        : metric?.value;

    return isMultiChannel
      ? metricsStore.getTargetMetricDetailsMultiChannel(metricKey, isContentReport)
      : metricsStore.getTargetMetricDetails(channels[0], metricReportTypeKey, metricKey);
  });

  const allMetricsAllowGoals = details.length > 0 && details.every((d) => d?.allow_goals);
  const dataTypes = new Set(details.map((d) => d?.data_type));
  const goalDataType = dataTypes.size === 1 ? [...dataTypes][0] : null;

  return {
    allMetricsAllowGoals,
    goalDataType,
    showGoals: allMetricsAllowGoals && !!goalDataType,
  };
}

export function cleanGoals(cleanedMeta) {
  if (!cleanedMeta.goals?.overall) return;

  const overall = cleanedMeta.goals.overall;

  if (!overall.value || !overall.type) {
    delete cleanedMeta.goals;
    return;
  }

  // Non-Duration
  if (overall.type !== DATA_TYPES.SECONDS && overall.type !== DATA_TYPES.MILLISECONDS) {
    if (!overall.value) {
      delete cleanedMeta.goals;
    } else {
      overall.value = toNumber(overall.value); // Should be a number from GoalInput validation
    }
    return;
  }

  // Duration
  const durationKeys = Object.values(DURATION_OPTIONS).map((option) => option.formKey);
  durationKeys.forEach((option) => {
    if (overall.value[option]) overall.value[option] = toNumber(overall.value[option]);
    else delete overall.value[option];
  });
  if (!durationKeys.some((option) => overall.value?.[option])) delete cleanedMeta.goals;
}

export function buildCleanedMetasMultiMetric(reportForm, youTubeAllowed = true) {
  const metricSet = new Set(reportForm.meta.metrics);
  return [...metricSet].reduce((acc, metric) => {
    const form = cloneDeep(reportForm);
    form.meta.metrics = [metric];
    if (reportForm.meta.metrics.length > 1) {
      reportForm.meta.aggregate[metric].forEach((aggregate) => {
        form.meta.aggregate = aggregate ? 'AVERAGE' : undefined;
        acc.push(getCleanedReportMeta(form, youTubeAllowed));
      });
    } else {
      acc.push(getCleanedReportMeta(form, youTubeAllowed));
    }
    return acc;
  }, []);
}

export function buildBatchReportPayload(reportForm, currentLayout, youTubeAllowed = true) {
  const cleanedMetas = buildCleanedMetasMultiMetric(reportForm, youTubeAllowed);
  const reports = [];
  const layoutPlacements = [];

  let runningLayout = currentLayout ?? [];
  cleanedMetas.forEach((meta, index) => {
    const clientRef = `r${index}`;
    const nextLayout = V1LayoutAdapter.addReport(runningLayout, {
      type: reportForm.type,
      clientRef,
    });
    const newItem = nextLayout[nextLayout.length - 1];
    if (newItem?.client_ref !== clientRef) return;

    reports.push({ client_ref: clientRef, type: reportForm.type, meta });
    layoutPlacements.push({
      client_ref: clientRef,
      x: newItem.x,
      y: newItem.y,
      w: newItem.w,
      h: newItem.h,
    });
    runningLayout = nextLayout;
  });

  return { reports, layout_placements: layoutPlacements };
}

export function getFirstReportIdInLayout(layout, reports) {
  if (!Array.isArray(layout) || !reports?.length) return undefined;
  const createdIds = new Set(reports.map((report) => report.id));
  const [firstItem] = layout
    .filter((item) => createdIds.has(item.report_id ?? item.reportId))
    .sort(V1LayoutAdapter.sortLayout);
  return firstItem?.report_id ?? firstItem?.reportId;
}

export function getCleanedReportMeta(reportForm, youTubeAllowed = true) {
  const cleanReportMeta = { ...reportForm.meta };
  const isTsiStackedBarMetricReport =
    cleanReportMeta.metrics?.[0] === TOTAL_TSI_METRIC &&
    reportForm.type === REPORTS.STACKED_BAR_METRIC.value;
  if (cleanReportMeta.metrics?.[0] === TOTAL_TSI_METRIC) {
    if (cleanReportMeta.aggregate_by === AGGREGATE_REPORT_TYPES.CHANNEL.value) {
      // YouTube compliance: drop YouTube from the saved TSI channel set while the flag is off.
      cleanReportMeta.channels = getTsiEnabledChannels(youTubeAllowed);
    } else {
      cleanReportMeta.channels = [CHANNELS.TSI_CHANNELS.value];
    }
    if (reportForm.type === LINE_GRAPH_REPORT_TYPES.GRAPH) {
      cleanReportMeta.static_dates = createTSICustomDateRange();
    }
  }

  cleanNestedMetaHelper(cleanReportMeta);
  cleanReportFilters(cleanReportMeta);
  cleanGoals(cleanReportMeta);
  if (reportForm.type === REPORTS.TOTAL_METRIC.value && !cleanReportMeta.aggregate_by) {
    cleanReportMeta.aggregate_by = METRIC_DISPLAY_OPTIONS.TOTAL.value;
  }

  if (
    reportForm.type === REPORTS.CREATOR_TOTAL_GROUPED_METRIC.value &&
    !cleanReportMeta.aggregate_by
  ) {
    cleanReportMeta.aggregate_by = AGGREGATE_REPORT_TYPES.BRAND.value;
  }

  if (isMultiMediaMetricReport(reportForm.type) || isTsiStackedBarMetricReport) {
    delete cleanReportMeta.aggregate_by;
  }

  if (reportForm.metricType === METRIC_TYPES.CUSTOM) {
    delete cleanReportMeta.channels;
  }

  if (
    isNil(cleanReportMeta.secondary_aggregate_by) ||
    cleanReportMeta.secondary_aggregate_by === '' ||
    isTsiStackedBarMetricReport
  ) {
    delete cleanReportMeta.secondary_aggregate_by;
  }

  const cleanReportMetaKeys = Object.keys(cleanReportMeta);
  if (cleanReportMetaKeys.includes('mediaTypes')) delete cleanReportMeta.mediaTypes;
  if (cleanReportMetaKeys.includes('mediaType')) delete cleanReportMeta.mediaType;
  if (cleanReportMetaKeys.includes('reportType')) delete cleanReportMeta.reportType;
  if (cleanReportMetaKeys.includes('earnedSourceType')) delete cleanReportMeta.earnedSourceType;
  if (cleanReportMetaKeys.includes('report_visualization'))
    delete cleanReportMeta.report_visualization;

  return cleanReportMeta;
}

export function extractBaseMetricsFromVariants(metrics, channels, reportType) {
  if (!metrics?.length) return [];
  const baseMetrics = metrics.map((metric) =>
    getMediaTypeMetricParentName(metric, channels, reportType),
  );
  return [...new Set(baseMetrics)];
}

export function getReportType(
  reportVisualization,
  reportType,
  reportCategory,
  selectedMediaTypes,
  selectedEarnedSourceType,
  secondaryAggregateBy,
  canAccessStatsEngineV2 = false,
) {
  // when the drawer unmounts we want to unset the reportType
  if (isNil(reportType)) return reportType;

  if (
    !canAccessStatsEngineV2 &&
    TOTAL_REPORT_TYPES.includes(reportType) &&
    selectedMediaTypes?.length > 1
  ) {
    return REPORTS.MULTI_METRIC_MEDIA_TYPE.value;
  }
  const isEarnedReportCategory = reportCategory === REPORT_CATEGORY_OPTIONS.EARNED.id;

  if (
    CONTENT_REPORT_TYPES.includes(reportType) &&
    selectedEarnedSourceType === UGC &&
    isEarnedReportCategory
  )
    return REPORTS.CONTENT_UGC.value;
  if (
    CONTENT_REPORT_TYPES.includes(reportType) &&
    selectedEarnedSourceType === CREATORS &&
    isEarnedReportCategory
  )
    return REPORTS.CREATOR_CONTENT.value;
  if (
    TOTAL_REPORT_TYPES.includes(reportType) &&
    selectedEarnedSourceType === CREATORS &&
    !reportVisualization &&
    isEarnedReportCategory
  )
    return REPORTS.CREATOR_TOTAL_METRIC.value;
  if (
    GRAPH_REPORTS.includes(reportType) &&
    selectedEarnedSourceType === CREATORS &&
    !reportVisualization &&
    isEarnedReportCategory
  )
    return REPORTS.CREATOR_LINE_GRAPH.value;

  // Set reportType for when secondary aggregate is set
  if (!isNil(secondaryAggregateBy) && secondaryAggregateBy === BASE_BREAKDOWN_OPTIONS.NONE.value) {
    if (reportVisualization === GRAPH_DISPLAY_OPTIONS_FOR_MENU.HORIZONTAL_BAR_CHART.value)
      return REPORTS.HORIZONTAL_BAR_METRIC.value;
    if (isEarnedReportCategory && selectedEarnedSourceType === CREATORS)
      return REPORTS.CREATOR_BAR_METRIC.value;
    return REPORTS.BAR_METRIC.value;
  }

  if (
    !isNil(secondaryAggregateBy) &&
    (COMMUNITY_STACKED_BAR_BREAKDOWN_OPTIONS.includes(secondaryAggregateBy) ||
      TSI_STACKED_BAR_BREAKDOWN_OPTIONS.includes(secondaryAggregateBy) ||
      LISTENING_STACKED_BAR_BREAKDOWN_OPTIONS.includes(secondaryAggregateBy) ||
      canAccessStatsEngineV2)
  ) {
    if (reportVisualization === GRAPH_DISPLAY_OPTIONS_FOR_MENU.HORIZONTAL_BAR_CHART.value)
      return REPORTS.HORIZONTAL_BAR_METRIC.value;
    return REPORTS.STACKED_BAR_METRIC.value;
  }

  // When stats engine v2 is enabled, use generic report types for competitive reports
  // instead of competitive-specific types (e.g., TOTAL_GROUPED_METRIC instead of COMPETITIVE_MULTI_BRAND_METRIC)
  const isCompetitiveCategory = reportCategory === REPORT_CATEGORY_OPTIONS.COMPETITIVE.id;
  const reportTypeMapping =
    canAccessStatsEngineV2 && isCompetitiveCategory
      ? REPORT_CATEGORY_TO_REPORT_TYPE.get(REPORT_CATEGORY_OPTIONS.OWNED.id)
      : REPORT_CATEGORY_TO_REPORT_TYPE.get(reportCategory);

  if (reportTypeMapping?.[selectedEarnedSourceType]) {
    return reportTypeMapping[selectedEarnedSourceType]?.get(reportVisualization) ?? reportType;
  }
  return reportTypeMapping?.get(reportVisualization) ?? reportType;
}

export { getReportVisualizationBasedOnReportType } from '@/app/dashboards/utils/reportVisualization.utils';

export function getBenchmarksInfo(benchmarks, isTsiBenchmark = false) {
  return benchmarks?.map((benchmark) => ({
    industry_id: benchmark.industry_id,
    follower_bucket: isTsiBenchmark ? benchmark.follower_bucket_id : benchmark.follower_bucket,
    ...(isTsiBenchmark && { type: BENCHMARK_TYPES.INDUSTRY }),
  }));
}

export function getReportFormValues(formValue) {
  return formValue?.map((item) => item.value) || [];
}

/**
 * Get metrics array for report meta, handling custom metrics, platform metrics,
 * and media type breakdowns
 * @param {Object} selectedMetrics - The selected metric object with value and other properties
 * @param {Object} customMetric - The selected custom metric if any
 * @param {Array} selectedMediaTypes - Array of selected media types
 * @param {boolean} canAccessStatsEngineV2 - When true, V2 stores the base metric in
 *   `meta.metrics` and the media type as a separate filter dimension, so the legacy
 *   substitution to media-type-specific metric names is skipped.
 * @returns {Array<string>} Array of metric strings for backend
 */
export function getMetrics(
  selectedMetrics,
  customMetric,
  selectedMediaTypes,
  canAccessStatsEngineV2 = false,
) {
  if (customMetric) {
    return [`${METRIC_PREFIX.CUSTOM}${customMetric.value}`];
  }

  if (Array.isArray(selectedMetrics)) {
    // V2 array shape: one entry per selected metric in single- or multi-select mode.
    // Media-type expansion is a V1 fallback only — V2 keeps media type as a separate filter.
    if (selectedMediaTypes?.length && !canAccessStatsEngineV2) {
      return selectedMediaTypes.map((mediaType) => mediaType.metricName);
    }
    return selectedMetrics.map((metric) => metric?.value).filter(Boolean);
  }

  if (!selectedMetrics?.value) {
    return [];
  }

  if (selectedMediaTypes && selectedMediaTypes.length > 0 && !canAccessStatsEngineV2) {
    return selectedMediaTypes.map((mediaType) => mediaType.metricName);
  }

  return [selectedMetrics.value];
}

export function getContentTagValues(contentTagSelectionMode, contentTags) {
  return contentTagSelectionMode === ALL_CONTENT || isEmpty(contentTags)
    ? []
    : contentTags.map((tag) => tag.id);
}

function getAdCampaignIds(adCampaigns) {
  if (!adCampaigns) return [];
  const selectedAdCampaigns = adCampaigns?.selectedAdCampaigns ?? [];
  if (!selectedAdCampaigns.length) return [];

  return selectedAdCampaigns.map((adCampaign) => adCampaign.value);
}

function getDisplayStrategy(displayStrategy) {
  return displayStrategy === DISPLAY_STRATEGY.PERCENT_OF_GRAND_TOTAL ? displayStrategy : null;
}

export function updateReportStoreWithForm({
  formState,
  reportForm,
  reportCategory,
  // Remove when removing newStatsEngineEnabled/canAccessStatsEngineV2 flag
  canAccessStatsEngineV2 = false,
}) {
  const {
    adAccounts,
    adCampaigns,
    aggregate,
    benchmarks,
    brands,
    channels,
    community,
    competitors,
    contentTags,
    contentTagSelectionMode,
    creators,
    customMetric,
    displayStrategy,
    earnedSourceType,
    goals,
    listening,
    mediaTypes: selectedMediaTypes,
    metrics: selectedMetrics,
    metricType,
    reportBreakdown,
    reportType: newReportType,
    reportVisualization,
    secondaryAggregateBy,
    sortOrder,
    staticDates,
    customName,
  } = formState;

  const competitorIds =
    competitors.selectedCompetitorTags?.length > 0
      ? getCompetitorSourceAccountIDsFromTags(competitors.selectedCompetitorTags)
      : getReportFormValues(competitors.selectedCompetitors);

  // Strip AVG_ prefix from metrics for backend when aggregate is AVERAGE
  let metricsForSave = selectedMetrics;

  if (Array.isArray(selectedMetrics)) {
    metricsForSave = selectedMetrics.map((metricOption) => ({
      ...metricOption,
      value: metricOption.isAveraged
        ? metricOption.value.slice(METRIC_PREFIX.AVERAGE.length)
        : metricOption.value,
    }));
  } else if (
    aggregate === AGGREGATION_OPTIONS.AVERAGE &&
    selectedMetrics?.value?.startsWith?.(METRIC_PREFIX.AVERAGE)
  ) {
    const strippedMetricValue = selectedMetrics.value.slice(METRIC_PREFIX.AVERAGE.length);

    // Validate that the underlying metric value is not empty after stripping prefix
    if (!strippedMetricValue) {
      metricsForSave = selectedMetrics;
    } else {
      // Remove isAveraged flag as it's UI-only
      const { isAveraged, ...metricsWithoutFlag } = selectedMetrics;
      metricsForSave = {
        ...metricsWithoutFlag,
        value: strippedMetricValue,
      };
    }
  }

  const type = getReportType(
    reportVisualization,
    newReportType,
    reportCategory,
    selectedMediaTypes,
    earnedSourceType,
    secondaryAggregateBy,
    canAccessStatsEngineV2,
  );

  const noneAggregateGraphReports = [...LINE_CHART_REPORTS];

  let aggregateBy;
  if (
    noneAggregateGraphReports.includes(type) &&
    reportCategory !== REPORT_CATEGORY_OPTIONS.SOCIAL_LISTENING.id
  ) {
    aggregateBy = reportBreakdown || null;
  } else if (STACKED_BAR_CHART_REPORTS.includes(type)) {
    aggregateBy = reportBreakdown || null;
  } else {
    aggregateBy = reportBreakdown ?? reportVisualization;
  }
  const displayAs = getDisplayStrategy(displayStrategy);

  const meta = {
    aggregate_by: aggregateBy,
    benchmarks: getBenchmarksInfo(
      benchmarks,
      Array.isArray(selectedMetrics)
        ? selectedMetrics?.[0]?.value === TOTAL_TSI_METRIC
        : selectedMetrics?.value === TOTAL_TSI_METRIC,
    ),
    brand_ids: getReportFormValues(brands.selectedBrands),
    brand_tags: getReportFormValues(brands.selectedBrandTags),
    channels,
    community: reportCategory === REPORT_CATEGORY_OPTIONS.COMMUNITY.id ? community : null,
    competitor_source_account_ids: competitorIds,
    competitor_tags: formatCompetitorTagsByBrand(competitors.selectedCompetitorTags),
    content_tag_ids: getContentTagValues(contentTagSelectionMode, contentTags),
    creator_ids: getReportFormValues(creators.selectedCreators),
    creator_tag_ids: getReportFormValues(creators.selectedCreatorTags),
    ...(displayAs ? { displayStrategy: displayAs } : {}),
    goals,
    listening: reportCategory === REPORT_CATEGORY_OPTIONS.SOCIAL_LISTENING.id ? listening : null,
    mediaTypes: getReportFormValues(selectedMediaTypes),
    // Drop the conditional spread when removing newStatsEngineEnabled/canAccessStatsEngineV2 flag
    // and write `media_types` unconditionally. Backend schema (sc-189957): top-level
    // `media_types` on ReportMetaSchema for Owned/Earned/Competitive reports.
    ...(canAccessStatsEngineV2 ? { media_types: getReportFormValues(selectedMediaTypes) } : {}),
    metrics: getMetrics(metricsForSave, customMetric, selectedMediaTypes, canAccessStatsEngineV2),
    secondary_aggregate_by:
      secondaryAggregateBy === BASE_BREAKDOWN_OPTIONS.NONE.value ? null : secondaryAggregateBy,
    sort_order: sortOrder,
    source_ad_account_ids: getReportFormValues(adAccounts),
    source_campaign_ids: getAdCampaignIds(adCampaigns),
    static_dates: staticDates,
    custom_name: customName,
    // UI-only mirror of formState.reportVisualization so the pure option
    // builders can read it off meta. Stripped before save by getCleanedReportMeta.
    report_visualization: reportVisualization ?? null,
  };

  if (Array.isArray(metricsForSave) && metricsForSave.length > 1) {
    // Map of {METRIC : [averaged/notAveraged]}
    meta.aggregate = metricsForSave.reduce((acc, metricOption) => {
      if (acc[metricOption.value]) {
        acc[metricOption.value].push(!!metricOption.isAveraged);
      } else {
        acc[metricOption.value] = [!!metricOption.isAveraged];
      }
      return acc;
    }, {});
  } else if (aggregate !== undefined) {
    meta.aggregate = aggregate;
  }

  // Set product_category by reverse-looking up from PRODUCT_CATEGORY_TO_REPORT_CATEGORY_MAP
  const productCategory = Object.entries(PRODUCT_CATEGORY_TO_REPORT_CATEGORY_MAP).find(
    (entry) => entry[1] === reportCategory,
  )?.[0];

  Object.assign(reportForm, {
    type,
    reportType: newReportType,
    metricType,
    earnedSourceType,
    meta,
    product_category: productCategory,
  });
}

export function getSortOrderText(reportSortOrder, channel, reportType, metricName) {
  const isTimeBased = metricName === 'Date Published';
  const sortOptions = isTimeBased ? DATE_PUBLISHED_SORT_ORDER_OPTIONS : SORT_ORDER_OPTIONS;

  const sortOrder = sortOptions.find((option) => option.value === reportSortOrder.toUpperCase());

  if (isTimeBased) {
    return sortOrder?.label || '';
  }

  if (reportType?.value === REPORTS.CREATOR_CONTENT.value) {
    return sortOrder?.creatorText || '';
  }
  if (channel === CHANNELS.INSTAGRAM_UGC.value) {
    return sortOrder?.ugcText || '';
  }
  if (channel === CHANNELS.INSTAGRAM_STORIES.value) {
    return sortOrder?.storiesText || '';
  }
  return sortOrder?.label || '';
}

export function reportComponent(reportType) {
  switch (REPORTS[reportType]?.components?.report?.name) {
    case 'GroupedMetricReportPanel':
      return SingleMetricReport;
    case 'MetricReportTablePanel':
      return TableMetricReport;
    case 'ContentReportPanel':
      return ContentReportPanel;
    case 'GraphReportPanel':
      return GraphReport;
    case 'HeaderReportPanel':
      return HeaderReportPanel;
    case 'VisionAiReportPanelPlaceholder':
      return VisionAiReportPanelPlaceHolder;
    case 'NoteReportPanel':
      return NoteReportPanel;
    default:
      return null;
  }
}

export function virtualScroll(options, scrollLimit) {
  return options && options.length > scrollLimit
    ? {
        height: VIRTUAL_SCROLLER_HEIGHT,
        itemSize: VIRTUAL_SCROLLER_ITEM_SIZE,
        bufferItems: VIRTUAL_SCROLLER_BUFFER,
      }
    : null;
}

export function filtersVirtualScroll(options, scrollLimit = FILTERS_VIRTUAL_SCROLLER_LIMIT) {
  return options && options.length > scrollLimit
    ? {
        height: FILTERS_VIRTUAL_SCROLLER_HEIGHT,
        itemSize: VIRTUAL_SCROLLER_ITEM_SIZE,
        bufferItems: VIRTUAL_SCROLLER_BUFFER,
      }
    : null;
}

export function parseCreatorId(creatorId) {
  return `CREATOR_${creatorId}`;
}

export function allSelectedCreatorsNotConnectedOrPending(selectedCreators) {
  if (!selectedCreators) return false;
  return selectedCreators.every(
    (creator) =>
      creator.status.status === CREATOR_STATUS.NOT_CONNECTED ||
      creator.status.status === CREATOR_STATUS.PENDING ||
      creator.status.status === CREATOR_STATUS.NOT_INVITED,
  );
}

export function oneOrMoreSelectedCreatorsConnected(selectedCreators) {
  if (!selectedCreators) return false;
  return selectedCreators.some((creator) => creator.status.status === CREATOR_STATUS.CONNECTED);
}

function allSelectedCreatorsConnected(selectedCreators) {
  if (!selectedCreators) return false;
  return selectedCreators.every((creator) => creator.status.status === CREATOR_STATUS.CONNECTED);
}

export function selectedCreatorsMixedStatus(selectedCreators) {
  return (
    oneOrMoreSelectedCreatorsConnected(selectedCreators) &&
    !allSelectedCreatorsConnected(selectedCreators)
  );
}

export function getCategoryFromProductCategory(reportProductCategory) {
  const reportCategoryId = PRODUCT_CATEGORY_TO_REPORT_CATEGORY_MAP[reportProductCategory] ?? null;
  const reportCategoryObject = Object.values(REPORT_CATEGORY_OPTIONS).find(
    (category) => category.id === reportCategoryId,
  );

  return reportCategoryObject;
}

export function formatGoal(type, value) {
  if (type === DATA_TYPE_TIME_UNITS.MILLISECONDS || type === DATA_TYPE_TIME_UNITS.SECONDS) {
    let label = '';
    let formattedValue = 0;
    const millisecondMultiplier = type === DATA_TYPE_TIME_UNITS.MILLISECONDS ? 1000 : 1;
    Object.values(DURATION_OPTIONS).forEach((option) => {
      if (value?.[option.formKey]) {
        label += `${value[option.formKey]}${option.shortHand} `;
        formattedValue += value[option.formKey] * option.secondMultiplier * millisecondMultiplier;
      }
    });
    return { label, value: formattedValue };
  }
  if (type === DATA_TYPES.PERCENTAGE) return { label: `${value}%`, value: value / 100 };
  if (type === DATA_TYPES.INTEGER) {
    const formattedValue = formatIntegerWithRules(value);
    return { label: formattedValue, value };
  }
  return { label: value, value };
}

export function getGoalChipProps(
  goals,
  percentageToGoal,
  title,
  current,
  formatConfig,
  isNegativeMetric = false,
) {
  const truncatedPercentage = percentageToGoal ? (percentageToGoal * 100).toFixed(0) : 0;
  const { value: formattedTarget } = formatGoal(goals.type, goals.value);

  return {
    targetValue: formattedTarget,
    currentValue: current,
    percentageOverride: truncatedPercentage,
    formatType: formatTypeMap[goals.type],
    format: formatConfig,
    title,
    negativeGoal: isNegativeMetric,
  };
}

export function getDashboardChannelIconName(channel) {
  if (channel === CHANNELS.INSTAGRAM_STORIES.value)
    return CHANNELS.INSTAGRAM_STORIES.dashingUiChannel;
  if (channel === CHANNELS.INSTAGRAM_STORIES_UGC.value)
    return CHANNELS.INSTAGRAM_STORIES_UGC.dashingUiChannelIcon;
  const channelMap = {
    INSTAGRAM: 'INSTAGRAM',
    FACEBOOK: 'FACEBOOK',
    YOUTUBE: 'YOUTUBE',
    LINKEDIN: 'LINKEDIN',
    TIKTOK: 'TIKTOK',
    TWITTER: 'TWITTER',
    PINTEREST: 'PINTEREST',
    META: 'META',
  };

  return Object.keys(channelMap).find((prefix) => channel?.startsWith(prefix)) ?? channel;
}

export function getBreakdownLabelWithFallback(value, breakdownType) {
  if (value == null) return `Unknown ${startCase(breakdownType)}`;

  const channelText = getChannelText(value);
  if (channelText && channelText !== value) return channelText;

  return `Unknown ${startCase(breakdownType)}`;
}

export function getBreakdownLabelFromValue(value) {
  return getBreakdownLabelWithFallback(value, 'item');
}

export function getBreakdownLabel(key, breakdownData, aggregateBy, isPrimary = true) {
  if (isPrimary) {
    if (breakdownData?.name) return breakdownData.name;
    if (aggregateBy === TOTAL_METRIC_GROUPS.CHANNEL) {
      return getBreakdownLabelWithFallback(key, 'channel');
    }
  }

  return getBreakdownLabelWithFallback(key, aggregateBy);
}

export function disableTabs(tabs, disabled) {
  return disabled ? tabs.map((tab) => ({ ...tab, disabled: true })) : tabs;
}
